import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import houseImage from "./house.jpg"; 

function Hero() {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [btnHover, setBtnHover] = useState(false);
  
  // Chatbot System UI States
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, sender: "bot", text: "Hello! Welcome to ZeroPix Studio AI Concierge. How can we elevate your architectural visuals or post-production brief today?" }
  ]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Comprehensive Cross-Platform Device Framework Breakpoints
  const isDesktopLarge = windowWidth > 1440;
  const isWeb = windowWidth > 1024;
  const isTablet = windowWidth <= 1024 && windowWidth > 768;
  const isIphonePortrait = windowWidth <= 430; // Native target specs for iPhone 14/15/16 Pro Max screens
  const isMobile = windowWidth <= 768;

  // Custom AI Node responses processing pipeline
  const handleOptionClick = (optionType) => {
    let userMsg = "";
    let botMsg = "";

    if (optionType === "about") {
      userMsg = "Tell me about ZeroPix Studio";
      botMsg = "ZeroPix is a premium high-end post-production studio founded in 2024. We engine surgical, studio-grade asset configurations, hyper-realistic CGI adjustments, and cinematic walkthrough frameworks with an elite 24H delivery matrix guarantee.";
    } else if (optionType === "order") {
      userMsg = "How to place an Order?";
      botMsg = "To dispatch an operational order layout, execute a brief click on 'VIEW OUR WORK' or routing maps on the Contact page. Our pipelines will capture target specs directly.";
    } else if (optionType === "contact") {
      userMsg = "Get Direct Support Details";
      botMsg = "Connect via terminal lines instantly! Corporate Dispatch Mail: contact@zeropix.com | Hot-Line Dial Link: +91 98765 43210.";
    }

    setMessages((prev) => [
      ...prev,
      { id: Date.now(), sender: "user", text: userMsg }
    ]);

    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, sender: "bot", text: botMsg }
      ]);
    }, 550);
  };

  const styles = {
    wrapper: {
      backgroundColor: "#fff",
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      flexDirection: isWeb ? "row" : "column-reverse", 
      alignItems: "center",
      justifyContent: isWeb ? "space-between" : "flex-end", // Fixed syntax syntax error here
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      position: "relative",
      overflow: "hidden",
    },

    left: {
      flex: 1,
      zIndex: 10,
      textAlign: isWeb ? "left" : "center",
      padding: isDesktopLarge ? "0 0 0 10%" : isWeb ? "0 0 0 8%" : isTablet ? "60px 40px" : "30px 20px 80px",
      width: "100%",
      boxSizing: "border-box",
    },

    mainTitle: {
      fontSize: isDesktopLarge ? "130px" : isWeb ? "110px" : isTablet ? "80px" : isIphonePortrait ? "42px" : "48px",
      fontWeight: "900",
      lineHeight: "0.85",
      color: "#111",
      letterSpacing: isWeb ? "-5px" : "-2px",
      marginBottom: "20px",
      textTransform: "uppercase"
    },

    outlineText: {
      WebkitTextStroke: isMobile ? "1px #111" : "2px #111",
      color: "transparent",
    },

    desc: {
      fontSize: isMobile ? "15px" : "18px",
      color: "#555",
      marginBottom: "40px",
      maxWidth: isWeb ? "500px" : "90%",
      marginInline: isWeb ? "0" : "auto",
      lineHeight: "1.6"
    },

    button: {
      padding: isWeb ? "22px 50px" : "18px 40px",
      background: btnHover ? "#ffcc00" : "#111",
      color: btnHover ? "#111" : "#fff",
      border: "none",
      cursor: "pointer",
      fontWeight: "bold",
      transition: "0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
      borderRadius: btnHover ? "50px" : "4px",
      fontSize: "14px",
      letterSpacing: "1px"
    },

    imageContainer: {
      position: isWeb ? "absolute" : "relative",
      right: 0,
      top: 0,
      width: isWeb ? "58%" : "100%",
      height: isWeb ? "100%" : isTablet ? "500px" : "350px",
      zIndex: 1
    },

    image: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },

    fade: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: isWeb
        ? "linear-gradient(to right, #fff 15%, rgba(255,255,255,0.3) 45%, transparent 100%)"
        : "linear-gradient(to top, #fff 10%, transparent 50%)"
    },

    label: {
      position: "absolute",
      bottom: isWeb ? "40px" : "20px",
      right: isWeb ? "40px" : "20px",
      background: "#ffcc00",
      padding: "10px 20px",
      fontWeight: "900",
      fontSize: "12px",
      zIndex: 5,
      boxShadow: "10px 10px 0px rgba(0,0,0,0.1)"
    },

    /* HIGH-FIDELITY LABORATORY WHITE ROBOT INTERIOR CHAT CONSOLE */
    botFloatingTrigger: {
      position: "fixed",
      bottom: isIphonePortrait ? "16px" : isMobile ? "24px" : "36px",
      right: isIphonePortrait ? "16px" : isMobile ? "24px" : "36px",
      width: isMobile ? "60px" : "68px",
      height: isMobile ? "60px" : "68px",
      backgroundColor: "#111",
      borderRadius: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      zIndex: 2147483647,
      boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
      border: "2px solid #ffcc00",
      transition: "transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    },
    chatConsoleContainer: {
      position: "fixed",
      bottom: isIphonePortrait ? "90px" : isMobile ? "100px" : "120px",
      right: isIphonePortrait ? "16px" : isMobile ? "24px" : "36px",
      width: isIphonePortrait ? "calc(100% - 32px)" : isMobile ? "calc(100% - 48px)" : "400px",
      height: isIphonePortrait ? "70vh" : isMobile ? "480px" : "530px",
      backgroundColor: "#ffffff", // Pure interior premium lab white look
      borderRadius: "24px",
      boxShadow: "0 30px 70px rgba(0,0,0,0.18)",
      border: "1px solid #111",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      zIndex: 2147483647,
      animation: "botFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards",
    },
    chatTopBarHeader: {
      backgroundColor: "#111", // Dark Robot Main Chassis Panel
      padding: isMobile ? "16px 20px" : "24px",
      display: "flex",
      alignItems: "center",
      gap: "18px",
      borderBottom: "4px solid #ffcc00",
    },
    // ROBOT HEAD EMBEDDED DIGITAL DISPLAY SIGNATURE BOX
    studioEliteLogo: {
  width: isDesktopLarge
    ? "82px"
    : isWeb
    ? "74px"
    : isTablet
    ? "68px"
    : isIphonePortrait
    ? "60px"
    : "64px",

  height: isDesktopLarge
    ? "82px"
    : isWeb
    ? "74px"
    : isTablet
    ? "68px"
    : isIphonePortrait
    ? "60px"
    : "64px",

  backgroundColor: "#ffcc00",
  borderRadius: isMobile ? "16px" : "20px",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  fontSize: isDesktopLarge
    ? "34px"
    : isWeb
    ? "30px"
    : isTablet
    ? "28px"
    : isIphonePortrait
    ? "24px"
    : "26px",

  fontWeight: "900",
  color: "#111",
  letterSpacing: "-1px",

  boxShadow: "0 8px 24px rgba(255, 204, 0, 0.35)",

  flexShrink: 0,
},
    metaHeaderText: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
    },
    brandMainHeading: {
      color: "#ffffff",
      fontSize: isMobile ? "15px" : "16px",
      fontWeight: "900",
      letterSpacing: "0.5px",
    },
    systemActivePulseLabel: {
      color: "#ffcc00",
      fontSize: "10px",
      fontWeight: "800",
      letterSpacing: "1px",
      marginTop: "3px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    terminateConsoleBtn: {
      background: "none",
      border: "none",
      color: "#ffffff",
      fontSize: "26px",
      cursor: "pointer",
      fontWeight: "300",
      opacity: 0.7,
      transition: "opacity 0.2s ease",
    },
    // Pristine Clean White Screen Scroller Interface
    chatTerminalScroller: {
      flex: 1,
      padding: isMobile ? "16px" : "24px",
      overflowY: "auto",
      backgroundColor: "#ffffff", // Clean White Canvas Viewport
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    bubbleEngineBot: {
      alignSelf: "flex-start",
      backgroundColor: "#f4f4f6", // Off-white clean text block
      color: "#111111",
      padding: "16px 20px",
      borderRadius: "0px 20px 20px 20px",
      fontSize: "14px",
      lineHeight: "1.6",
      maxWidth: "88%",
      fontWeight: "500",
      border: "1px solid #eaeaea",
    },
    bubbleEngineUser: {
      alignSelf: "flex-end",
      backgroundColor: "#111111",
      color: "#ffffff",
      padding: "16px 20px",
      borderRadius: "20px 20px 0px 20px",
      fontSize: "14px",
      lineHeight: "1.6",
      maxWidth: "88%",
      fontWeight: "600",
    },
    actionOptionsFooterPanel: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      padding: isMobile ? "12px 20px 20px" : "0 24px 24px 24px",
      backgroundColor: "#ffffff",
    },
    customActionChipNode: {
      backgroundColor: "#ffffff",
      border: "2px solid #111111",
      padding: "14px 20px",
      borderRadius: "14px",
      fontSize: "13px",
      fontWeight: "800",
      color: "#111111",
      cursor: "pointer",
      textAlign: "left",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      transition: "all 0.25s ease",
    }
  };

  return (
    <main style={styles.wrapper}>
      
      {/* SEAMLESS MULTI-PLATFORM RESPONSIVE FRAMEWORK OVERRIDES */}
      <style>{`
        @keyframes botFadeIn {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .bot-bubble-trigger-node:hover {
          transform: scale(1.08) rotate(-3deg);
        }
        .interactive-action-node:hover {
          background-color: #ffcc00 !important;
          transform: translateX(4px);
        }
        .terminate-btn-node:hover {
          opacity: 1 !important;
        }
      `}</style>

      {/* LEFT CONTENT AREA */}
      <div style={styles.left}>
        <h1 style={styles.mainTitle}>
          BEYOND <br />
          <span style={styles.outlineText}>REALITY.</span>
        </h1>

        <p style={styles.desc}>
          Premium post-production for the world's most ambitious architects and realtors. 
          We bring your vision to life with precision.
        </p>

        <button
          style={styles.button}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          onClick={() => navigate("/portfolio")}
        >
          VIEW OUR WORK &rarr;
        </button>
      </div>

      {/* IMAGE AREA */}
      <div style={styles.imageContainer}>
        <img src={houseImage} alt="Modern House" style={styles.image} />
        <div style={styles.fade}></div>
        <div style={styles.label}>EST. 2024 | 24H DELIVERY</div>
      </div>

      {/* FLOATING ACTION TRIGGER BUBBLE CONTROL */}
      <div 
        style={styles.botFloatingTrigger} 
        onClick={() => setIsOpen(!isOpen)}
        className="bot-bubble-trigger-node"
      >
        {isOpen ? (
          <span style={{ fontSize: "26px", color: "#ffcc00", fontWeight: "bold" }}>✕</span>
        ) : (
          <span style={{ fontSize: "28px", color: "#ffcc00" }}>💬</span>
        )}
      </div>

      {/* WEB, ANDROID, IPHONE AND DESKTOP COMPATIBLE RENDER ENGINE */}
      {isOpen && (
        <div style={styles.chatConsoleContainer}>
          
          {/* CRITICAL ROBOT HEAD HEADER CONTROL */}
          <div style={styles.chatTopBarHeader}>
            {/* ENLARGED BRAND LOGO GRAPHIC SCREEN AS REQUESTED: "ZP" */}
            <div style={styles.studioEliteLogo}>ZP</div>
            
            <div style={styles.metaHeaderText}>
              <span style={styles.brandMainHeading}>ZeroPix Concierge</span>
              <span style={styles.systemActivePulseLabel}>
                <span style={{ width: "7px", height: "7px", backgroundColor: "#00ff66", borderRadius: "50%", display: "inline-block" }}></span>
                AI RE-ROUTING OPERATIONAL
              </span>
            </div>
            
            <button 
              style={styles.terminateConsoleBtn} 
              onClick={() => setIsOpen(false)}
              className="terminate-btn-node"
            >
              ×
            </button>
          </div>

          {/* MESSAGE CANVAS VIEWPORT */}
          <div style={styles.chatTerminalScroller}>
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                style={msg.sender === "bot" ? styles.bubbleEngineBot : styles.bubbleEngineUser}
              >
                {msg.text}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* ORIGINAL USER TELEMETRY OPTION SELECTION PANEL */}
          <div style={styles.actionOptionsFooterPanel}>
            <div 
              style={styles.customActionChipNode} 
              className="interactive-action-node"
              onClick={() => handleOptionClick("about")}
            >
              <span>🏢 Tell me about ZeroPix Studio</span>
              <span>→</span>
            </div>
            
            <div 
              style={styles.customActionChipNode} 
              className="interactive-action-node"
              onClick={() => handleOptionClick("order")}
            >
              <span>📁 How to place an Order?</span>
              <span>→</span>
            </div>
            
            <div 
              style={styles.customActionChipNode} 
              className="interactive-action-node"
              onClick={() => handleOptionClick("contact")}
            >
              <span>📞 Get Direct Support Details</span>
              <span>→</span>
            </div>
          </div>
        </div>
      )}

    </main>
  );
}

export default Hero;